import type { RefObject } from 'react';
import { useEffect, useRef } from 'react';
import type { PDFViewer } from 'pdfjs-dist/web/pdf_viewer.mjs';

import type { PdfFitMode } from './pdfViewerScale';
import { getPdfViewerZoomOrigin, zoomPdfViewer } from './pdfViewerScale';

type UsePdfViewerGesturesOptions = {
  containerRef: RefObject<HTMLDivElement | null>;
  viewerRef: RefObject<PDFViewer | null>;
  fitModeRef: RefObject<PdfFitMode>;
  isHandTool: boolean;
  cancelPageJump: () => void;
  onPanChange?: (isPanning: boolean) => void;
};

/**
 * Adds cursor-centered wheel zooming and pointer-based scroll panning.
 */
export function usePdfViewerGestures({
  containerRef,
  viewerRef,
  fitModeRef,
  isHandTool,
  cancelPageJump,
  onPanChange,
}: UsePdfViewerGesturesOptions) {
  const onPanChangeRef = useRef(onPanChange);
  onPanChangeRef.current = onPanChange;
  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }
    const handleWheel = (event: WheelEvent) => {
      cancelPageJump();
      if (event.ctrlKey || event.metaKey) {
        event.preventDefault();
        const viewer = viewerRef.current;
        if (viewer) {
          fitModeRef.current = null;
          zoomPdfViewer(
            viewer,
            event.deltaY < 0 ? 'in' : 'out',
            getPdfViewerZoomOrigin(container, event.clientX, event.clientY)
          );
        }
      }
    };
    container.addEventListener('wheel', handleWheel, { passive: false });
    return () => container.removeEventListener('wheel', handleWheel);
  }, [cancelPageJump, containerRef, fitModeRef, viewerRef]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }
    let activePointerId: number | null = null;
    let startX = 0;
    let startY = 0;
    let startLeft = 0;
    let startTop = 0;
    const safelyReleasePointerCapture = (pointerId: number) => {
      try {
        if (container.hasPointerCapture(pointerId)) {
          container.releasePointerCapture(pointerId);
        }
      } catch {
        // Pointer capture may already have been released by the browser.
      }
    };
    const endPan = (event: PointerEvent, releaseCapture: boolean) => {
      if (event.pointerId !== activePointerId) {
        return;
      }
      activePointerId = null;
      if (releaseCapture) {
        safelyReleasePointerCapture(event.pointerId);
      }
      onPanChangeRef.current?.(false);
    };
    const pointerDown = (event: PointerEvent) => {
      if (
        activePointerId !== null ||
        !((isHandTool && event.button === 0) || event.button === 1)
      ) {
        return;
      }
      cancelPageJump();
      activePointerId = event.pointerId;
      startX = event.clientX;
      startY = event.clientY;
      startLeft = container.scrollLeft;
      startTop = container.scrollTop;
      try {
        container.setPointerCapture(event.pointerId);
      } catch {
        // Panning still works while the pointer remains over the container.
      }
      onPanChangeRef.current?.(true);
      event.preventDefault();
    };
    const pointerMove = (event: PointerEvent) => {
      if (event.pointerId === activePointerId) {
        container.scrollLeft = startLeft - (event.clientX - startX);
        container.scrollTop = startTop - (event.clientY - startY);
      }
    };
    const pointerUp = (event: PointerEvent) => endPan(event, true);
    const pointerCancel = (event: PointerEvent) => endPan(event, true);
    const lostPointerCapture = (event: PointerEvent) => endPan(event, false);
    container.addEventListener('pointerdown', pointerDown);
    container.addEventListener('pointermove', pointerMove);
    container.addEventListener('pointerup', pointerUp);
    container.addEventListener('pointercancel', pointerCancel);
    container.addEventListener('lostpointercapture', lostPointerCapture);
    return () => {
      if (activePointerId !== null) {
        safelyReleasePointerCapture(activePointerId);
        activePointerId = null;
      }
      container.removeEventListener('pointerdown', pointerDown);
      container.removeEventListener('pointermove', pointerMove);
      container.removeEventListener('pointerup', pointerUp);
      container.removeEventListener('pointercancel', pointerCancel);
      container.removeEventListener('lostpointercapture', lostPointerCapture);
      onPanChangeRef.current?.(false);
    };
  }, [cancelPageJump, containerRef, isHandTool]);
}
